import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  TextField,
  FormControl,
  RadioGroup,
  CircularProgress,
  Fade,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  Receipt as QuoteIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon,
  LocalShipping as DeliveryIcon,
  Store as PickupIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { usePdvStyles } from '../styles/pdvStyles';
import PdvCartItem from './PdvCartItem';
import PdvPaymentSelector from './PdvPaymentSelector';

const useLocalStyles = makeStyles((theme) => ({
  // Usa mesmas dimensões do pdvStyles.cartDrawer
  contentWrapper: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  slideContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 12,
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  deliveryOption: {
    flex: 1,
    padding: 12,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.action.hover,
    },
  },
  deliveryOptionSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    backgroundColor: theme.palette.primary.main + '15',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    marginBottom: 16,
    backgroundColor: theme.palette.error.main + '10',
    border: `2px solid ${theme.palette.error.main}`,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.error.main + '20',
    },
  },
  addressBox: {
    marginTop: 12,
    padding: 12,
    backgroundColor: theme.palette.grey[100],
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

const PdvSalesDrawer = ({
  open,
  onClose,
  // Cart props
  cart = [],
  selectedIndex = -1,
  onSelectItem,
  onUpdateQuantity,
  onRemoveItem,
  onApplyDiscount,
  onClearCart,
  totals = {},
  listRef,
  recoveredQuoteId = null,
  // Checkout props
  paymentMethod = 'pix',
  onPaymentMethodChange,
  changeAmount = 0,
  onChangeAmountChange,
  deliveryType = 'pickup',
  onDeliveryTypeChange,
  deliveryFee = 0,
  isFreeDelivery = false,
  notes = '',
  onNotesChange,
  internalNotes = '',
  onInternalNotesChange,
  isCustomerIdentified = false,
  customerAddress = null,
  customerAddresses = [],
  selectedAddressId = null,
  onAddressChange,
  onIdentifyCustomer,
  onFinalizeSale,
  onSaveQuote,
  isSending = false,
  isSavingQuote = false,
  containerRef,
}) => {
  const classes = usePdvStyles();
  const localClasses = useLocalStyles();
  const theme = useTheme();

  // Step state: 'cart' or 'checkout'
  const [step, setStep] = useState('cart');
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reset to cart step when drawer closes
  useEffect(() => {
    if (!open) {
      // Small delay to reset after close animation
      const timer = setTimeout(() => {
        setStep('cart');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Calculate final total
  const deliveryCharge = deliveryType === 'delivery' && !isFreeDelivery ? Number(deliveryFee) || 0 : 0;
  const finalTotal = (Number(totals.total) || 0) + deliveryCharge;
  const canFinalize = isCustomerIdentified && !isSending;

  // Handle step transitions - mais suave
  const goToCheckout = useCallback(() => {
    if (cart.length === 0) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setStep('checkout');
      setIsTransitioning(false);
    }, 100);
  }, [cart.length]);

  const goToCart = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setStep('cart');
      setIsTransitioning(false);
    }, 100);
  }, []);

  const handleItemClick = useCallback(
    (index) => {
      onSelectItem(index);
    },
    [onSelectItem]
  );

  // Auto-scroll to selected item
  useEffect(() => {
    if (open && step === 'cart' && selectedIndex >= 0 && listRef?.current) {
      const items = listRef.current.querySelectorAll('[data-cart-index]');
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [open, step, selectedIndex, listRef]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={classes.cartDrawer}
      transitionDuration={{ enter: 250, exit: 200 }}
      ModalProps={{
        container: containerRef?.current,
        style: { position: 'absolute', inset: 0 },
        keepMounted: false,
      }}
      BackdropProps={{
        style: { position: 'absolute', inset: 0 },
      }}
      PaperProps={{
        style: {
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Content Wrapper with Transitions */}
      <Box className={localClasses.contentWrapper}>
        {/* Cart Step */}
        <Fade in={step === 'cart' && !isTransitioning} timeout={300}>
          <Box
            className={localClasses.slideContainer}
            style={{
              visibility: step === 'cart' ? 'visible' : 'hidden',
              pointerEvents: step === 'cart' ? 'auto' : 'none',
            }}
          >
            {/* Cart Header */}
            <Box className={classes.cartDrawerHeader}>
              <Box className={classes.cartDrawerTitle}>
                <CartIcon />
                <Typography variant="h6" component="span">
                  Carrinho
                </Typography>
                <Typography variant="body2" component="span" style={{ opacity: 0.8 }}>
                  ({cart.length} {cart.length === 1 ? 'item' : 'itens'})
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose} style={{ color: 'inherit' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Quote Indicator */}
            {recoveredQuoteId && (
              <Box className={classes.quoteIndicator} style={{ margin: 8 }}>
                <QuoteIcon fontSize="small" />
                <Typography variant="caption">
                  Orcamento #{recoveredQuoteId}
                </Typography>
              </Box>
            )}

            {/* Cart Items */}
            <Box ref={listRef} className={classes.cartDrawerContent}>
              {cart.length === 0 ? (
                <Box className={classes.emptyState}>
                  <CartIcon style={{ fontSize: 24, color: theme.palette.divider, marginBottom: 8 }} />
                  <Typography className={classes.emptyStateText}>
                    Carrinho vazio
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Adicione produtos para iniciar a venda
                  </Typography>
                </Box>
              ) : (
                cart.map((item, index) => (
                  <PdvCartItem
                    key={item.id + '-' + index}
                    item={item}
                    index={index}
                    isSelected={index === selectedIndex}
                    onUpdateQuantity={onUpdateQuantity}
                    onRemove={onRemoveItem}
                    onApplyDiscount={onApplyDiscount}
                    onClick={handleItemClick}
                  />
                ))
              )}
            </Box>

            {/* Cart Footer */}
            {cart.length > 0 && (
              <Box className={classes.cartDrawerFooter} style={{ padding: 12 }}>
                {/* Resumo compacto - mesmo layout do checkout */}
                <Box style={{ marginBottom: 12, padding: '8px 12px', backgroundColor: theme.palette.grey[100], borderRadius: 6 }}>
                  <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                    <span>Subtotal ({totals.itemCount || 0} itens)</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </Box>
                  {totals.itemsDiscount > 0 && (
                    <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: theme.palette.success.main }}>
                      <span>Descontos</span>
                      <span>-{formatCurrency(totals.itemsDiscount)}</span>
                    </Box>
                  )}
                  <Divider style={{ margin: '6px 0' }} />
                  <Box style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem' }}>
                    <span>Total</span>
                    <span style={{ color: theme.palette.primary.main }}>{formatCurrency(totals.total)}</span>
                  </Box>
                </Box>

                {/* Botao principal - padrao igual ao checkout */}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={goToCheckout}
                  disabled={cart.length === 0}
                  endIcon={<ArrowForwardIcon />}
                  style={{
                    marginBottom: 8,
                    height: 48,
                    fontWeight: 600,
                    fontSize: 15,
                    borderRadius: 6,
                  }}
                >
                  Confirmar Pedido (F4)
                </Button>

                {/* Botoes secundarios - padrao igual ao checkout */}
                <Box style={{ display: 'flex', gap: 8 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={onClearCart}
                    startIcon={<ClearIcon />}
                    style={{
                      height: 40,
                      color: theme.palette.error.main,
                      borderColor: theme.palette.error.main,
                      borderRadius: 6,
                      fontSize: '0.875rem',
                    }}
                  >
                    Limpar
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={onClose}
                    startIcon={<ArrowBackIcon />}
                    style={{
                      height: 40,
                      color: theme.palette.text.secondary,
                      borderColor: theme.palette.divider,
                      borderRadius: 6,
                      fontSize: '0.875rem',
                    }}
                  >
                    Voltar
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>

        {/* Checkout Step */}
        <Fade in={step === 'checkout' && !isTransitioning} timeout={300}>
          <Box
            className={localClasses.slideContainer}
            style={{
              visibility: step === 'checkout' ? 'visible' : 'hidden',
              pointerEvents: step === 'checkout' ? 'auto' : 'none',
            }}
          >
            {/* Checkout Header */}
            <Box className={classes.cartDrawerHeader}>
              <Box className={classes.cartDrawerTitle}>
                <IconButton
                  size="small"
                  onClick={goToCart}
                  style={{ color: 'inherit', marginRight: 4 }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <PaymentIcon />
                <Typography variant="h6" component="span">
                  Finalizar Venda
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose} style={{ color: 'inherit' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Checkout Content - sem scroll */}
            <Box className={classes.cartDrawerContent} style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
              {/* Row 1: Delivery Type (compact) */}
              <Box>
                <Typography variant="caption" style={{ fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 6, display: 'block' }}>
                  Tipo de Entrega
                </Typography>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={deliveryType}
                    onChange={(e) => onDeliveryTypeChange && onDeliveryTypeChange(e.target.value)}
                  >
                    <Box style={{ display: 'flex', gap: 8 }}>
                      <Box
                        onClick={() => onDeliveryTypeChange && onDeliveryTypeChange('pickup')}
                        className={`${localClasses.deliveryOption} ${deliveryType === 'pickup' ? localClasses.deliveryOptionSelected : ''}`}
                        style={{ padding: 8, gap: 4 }}
                      >
                        <PickupIcon style={{ color: deliveryType === 'pickup' ? theme.palette.primary.main : theme.palette.text.secondary, fontSize: 24 }} />
                        <Typography variant="caption" style={{ fontWeight: deliveryType === 'pickup' ? 600 : 400 }}>
                          Retirada
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => onDeliveryTypeChange && onDeliveryTypeChange('delivery')}
                        className={`${localClasses.deliveryOption} ${deliveryType === 'delivery' ? localClasses.deliveryOptionSelected : ''}`}
                        style={{ padding: 8, gap: 4 }}
                      >
                        <DeliveryIcon style={{ color: deliveryType === 'delivery' ? theme.palette.primary.main : theme.palette.text.secondary, fontSize: 24 }} />
                        <Typography variant="caption" style={{ fontWeight: deliveryType === 'delivery' ? 600 : 400 }}>
                          Entrega {deliveryFee > 0 && !isFreeDelivery ? `+${formatCurrency(deliveryFee)}` : isFreeDelivery ? '(Grátis)' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </RadioGroup>
                </FormControl>

                {/* Delivery address selector */}
                {deliveryType === 'delivery' && (
                  <Box style={{ marginTop: 8 }}>
                    {customerAddresses && customerAddresses.length > 1 ? (
                      // Multiple addresses - show selector
                      <FormControl fullWidth size="small" variant="outlined">
                        <InputLabel id="address-select-label">Endereço de entrega</InputLabel>
                        <Select
                          labelId="address-select-label"
                          value={selectedAddressId || ''}
                          onChange={(e) => onAddressChange && onAddressChange(e.target.value)}
                          label="Endereço de entrega"
                          style={{ backgroundColor: '#fff' }}
                        >
                          {customerAddresses.map((addr) => (
                            <MenuItem key={addr.id} value={addr.id}>
                              <Box>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                  {addr.label || 'Endereço'}
                                  {addr.isDefault && ' (Principal)'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {addr.street}, {addr.number} - {addr.neighborhood}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : customerAddress ? (
                      // Single address - show directly
                      <Box style={{ padding: '8px 10px', backgroundColor: theme.palette.primary.main + '15', borderRadius: 6, border: `1px solid ${theme.palette.primary.light}` }}>
                        <Typography variant="caption" style={{ color: theme.palette.primary.dark, fontWeight: 600, display: 'block', marginBottom: 2 }}>
                          {customerAddress.label || 'Endereço de entrega'}:
                        </Typography>
                        <Typography variant="body2" style={{ color: theme.palette.text.primary, fontSize: '0.8rem', lineHeight: 1.3 }}>
                          {customerAddress.street}, {customerAddress.number}
                          {customerAddress.complement && ` - ${customerAddress.complement}`}
                          {' - '}{customerAddress.neighborhood}
                          {customerAddress.city && `, ${customerAddress.city}`}
                          {customerAddress.state && `/${customerAddress.state}`}
                          {customerAddress.zipCode && ` - CEP: ${customerAddress.zipCode}`}
                        </Typography>
                      </Box>
                    ) : (
                      // No address
                      <Box style={{ padding: '8px 10px', backgroundColor: theme.palette.warning.main + '15', borderRadius: 6, border: `1px solid ${theme.palette.warning.light}` }}>
                        <Typography variant="caption" style={{ color: theme.palette.warning.dark, fontWeight: 500 }}>
                          Nenhum endereço cadastrado. Clique em "Identificar cliente" para adicionar.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Row 2: Payment Method (compact) */}
              <Box>
                <PdvPaymentSelector
                  selectedMethod={paymentMethod}
                  onSelectMethod={onPaymentMethodChange}
                  total={finalTotal}
                  changeAmount={changeAmount}
                  onChangeAmountChange={onChangeAmountChange}
                />
              </Box>

              {/* Row 3: Notes - um embaixo do outro */}
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  variant="outlined"
                  label="Observações do cliente"
                  placeholder="Observações visíveis para o cliente..."
                  value={notes}
                  onChange={(e) => onNotesChange && onNotesChange(e.target.value)}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  variant="outlined"
                  label="Observações internas"
                  placeholder="Observações de uso interno..."
                  value={internalNotes}
                  onChange={(e) => onInternalNotesChange && onInternalNotesChange(e.target.value)}
                />
              </Box>

              {/* Customer identification warning (compact) */}
              {!isCustomerIdentified && (
                <Box
                  onClick={onIdentifyCustomer}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: 8,
                    backgroundColor: theme.palette.error.main + '15',
                    border: `1px solid ${theme.palette.error.main}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  <WarningIcon style={{ color: theme.palette.error.main, fontSize: 20 }} />
                  <Typography variant="caption" style={{ color: theme.palette.error.dark, fontWeight: 600 }}>
                    Clique para identificar cliente (CPF, Endereco)
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Checkout Footer - Padronizado com Cart Footer */}
            <Box className={classes.cartDrawerFooter} style={{ padding: 12 }}>
              {/* Resumo compacto - mesmo layout do carrinho */}
              <Box style={{ marginBottom: 12, padding: '8px 12px', backgroundColor: theme.palette.grey[100], borderRadius: 6 }}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                  <span>Subtotal ({totals.itemCount || 0} itens)</span>
                  <span>{formatCurrency(totals.subtotal)}</span>
                </Box>
                {totals.itemsDiscount > 0 && (
                  <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: theme.palette.success.main }}>
                    <span>Descontos</span>
                    <span>-{formatCurrency(totals.itemsDiscount)}</span>
                  </Box>
                )}
                {deliveryType === 'delivery' && (
                  <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: isFreeDelivery ? theme.palette.success.main : theme.palette.text.secondary }}>
                    <span>Entrega</span>
                    <span>{isFreeDelivery ? 'Gratis' : formatCurrency(deliveryFee)}</span>
                  </Box>
                )}
                <Divider style={{ margin: '6px 0' }} />
                <Box style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem' }}>
                  <span>Total</span>
                  <span style={{ color: theme.palette.primary.main }}>{formatCurrency(finalTotal)}</span>
                </Box>
              </Box>

              {/* Botao principal - padrao igual ao carrinho */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={onFinalizeSale}
                disabled={!canFinalize}
                endIcon={isSending ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                style={{
                  marginBottom: 8,
                  height: 48,
                  fontWeight: 600,
                  fontSize: 15,
                  borderRadius: 6,
                }}
              >
                {isSending ? 'Enviando...' : 'Concluir Venda'}
              </Button>

              {/* Botoes secundarios - padrao igual ao carrinho */}
              <Box style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={onSaveQuote}
                  disabled={isSavingQuote}
                  startIcon={isSavingQuote ? <CircularProgress size={16} /> : <QuoteIcon />}
                  style={{
                    height: 40,
                    borderRadius: 6,
                    fontSize: '0.875rem',
                  }}
                >
                  {isSavingQuote ? 'Salvando...' : 'Orcamento'}
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={goToCart}
                  startIcon={<ArrowBackIcon />}
                  style={{
                    height: 40,
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    borderRadius: 6,
                    fontSize: '0.875rem',
                  }}
                >
                  Voltar
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>
    </Drawer>
  );
};

export default React.memo(PdvSalesDrawer);
